import { DateOnly, addDays } from '@if/core/date'
import { EPaymentFrequency } from '@if/core/types'
import { EPaymentMethod } from '@src/types/constants'
import { ControlledDateOnlyPicker, ControlledSelect, ControlledTextField, ErrorAlert, NumberField } from '@if/mui'
import { Stack } from '@mui/material'
import { useComputeCalendarAction } from '@src/api/loan/loan-api'
import FormDialog from '@src/components/Common/Dialog/FormDialog'
import { useUser } from '@src/providers/UserProvider'
import { useUnmountableDialog } from '@src/services/dialog'
import { ECalendarSimulationAction, SignedCalendarSimulation } from '@src/types/loan/loan-simulation'
import { FormEventHandler, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import LoanCalendarActionPreviewTable from '../LoanCalendarActionPreview/LoanCalendarActionPreviewTable'
import { useValidPaymentOperationsForCalendarShift } from './LoanMoveCalendar-hooks'
import { MoveCalendarSchemaType, useMoveCalendarResolver } from './LoanMoveCalendar-schema'

interface LoanMoveCalendarDialogProps {
  initialSignedSimulation: SignedCalendarSimulation
  onConfirm: (data: SignedCalendarSimulation) => void
  onClose: () => void
  isLockedByOther?: boolean
}

const LoanMoveCalendarDialog = ({
  initialSignedSimulation,
  onConfirm,
  onClose,
  isLockedByOther = false,
}: LoanMoveCalendarDialogProps) => {
  const { t } = useTranslation()
  const user = useUser()
  const canBypassAcpLock = user?.rights.canEditLockedPpaPayments ?? false
  const {
    mutateAsync: computeAction,
    error,
    isPending,
  } = useComputeCalendarAction(
    initialSignedSimulation.simulation.fullLoan.loan.id,
    initialSignedSimulation.simulation.fullLoan.loan.financingProgramId,
  )
  const { dialogProps, closeDialog } = useUnmountableDialog(onClose, isPending)
  const [numberOfPaymentsToRaise, setNumberOfPaymentsToRaise] = useState<number>(0)
  const [amountToRaise, setAmountToRaise] = useState<number>(0)
  const [newSimulationWithSignature, setNewSimulationWithSignature] =
    useState<SignedCalendarSimulation>(initialSignedSimulation)

  const originalSimulation = initialSignedSimulation.simulation
  const resolver = useMoveCalendarResolver()
  const defaultValues = {
    fromDate: null,
    toDate: null,
    reason: null,
    paymentToRaiseIds: [],
  } as unknown as MoveCalendarSchemaType

  const {
    control,
    watch,
    formState: { isValid, isValidating, isSubmitting },
    setValue,
  } = useForm({
    resolver,
    mode: 'onChange',
    defaultValues,
    context: { simulation: originalSimulation, user: user },
  })

  const paymentOptions = useValidPaymentOperationsForCalendarShift(originalSimulation)
  const { fromDate, toDate, reason, paymentToRaiseIds } = watch()
  const minShiftPaymentDate = useMemo(
    () => originalSimulation.getNextValidActionDate(EPaymentMethod.PreAuthorizedPayment, canBypassAcpLock),
    [originalSimulation, canBypassAcpLock],
  )
  const maxShiftPaymentDate = useMemo(() => {
    if (fromDate == null) return minShiftPaymentDate
    const paymentConfig = originalSimulation.getRepaymentConfigAtDate(fromDate)

    if (paymentConfig == null) return minShiftPaymentDate

    const nextPayment = originalSimulation.getNextPaymentAfterDate(fromDate)
    let maxDate = fromDate
    if (paymentConfig.frequency === EPaymentFrequency.Monthly) {
      maxDate = addDays(fromDate, 30)
    } else if (paymentConfig.frequency === EPaymentFrequency.BiWeekly) {
      maxDate = addDays(fromDate, 7)
    }

    if (!nextPayment) return maxDate

    return nextPayment && nextPayment.effectiveDate.getTime() < maxDate.getTime()
      ? addDays(nextPayment.effectiveDate, -1)
      : maxDate
  }, [originalSimulation, fromDate, minShiftPaymentDate])

  const moveToFutur = fromDate && toDate && fromDate.getTime() < toDate.getTime()

  useEffect(() => {
    if (!moveToFutur) {
      setValue('paymentToRaiseIds', [], { shouldValidate: true })
      setNumberOfPaymentsToRaise(0)
      setAmountToRaise(0)
    }
  }, [moveToFutur, setValue])

  useEffect(() => {
    if (fromDate) {
      setValue('toDate', fromDate)
    }
  }, [fromDate, setValue])

  useEffect(() => {
    if (isValid && !isPending && !isValidating && !isSubmitting) {
      // Use setTimeout to defer the API call to the next tick
      // This is to ensure that the form state is fully updated before making the API call
      const timeoutId = setTimeout(() => {
        void computeAction({
          ...initialSignedSimulation,
          action: {
            actionType: ECalendarSimulationAction.MoveCalendar,
            fromDate,
            toDate,
            reason,
            paymentToRaiseIds,
          },
        }).then((data) => {
          setNewSimulationWithSignature(data)

          if (paymentToRaiseIds && paymentToRaiseIds.length > 0) {
            const operationId = paymentToRaiseIds[0]
            const originalAmount = originalSimulation.customerPaymentOperations.find(
              (op) => op.operation.id === operationId,
            )!.operation.amount

            const newAmount = data.simulation.customerPaymentOperations.find((op) => op.operation.id === operationId)!
              .operation.amount
            setAmountToRaise(newAmount - originalAmount)
          }
        })
      }, 0)

      return () => clearTimeout(timeoutId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate, reason, paymentToRaiseIds, isValid, isValidating])

  const handleOnNumberOfPaymentsToRaiseChanged = (value: string) => {
    const newNumber = Number(value)

    if (newNumber < 0) {
      setNumberOfPaymentsToRaise(0)
    } else if (newNumber > 6) {
      setNumberOfPaymentsToRaise(6)
    } else {
      setNumberOfPaymentsToRaise(newNumber)
    }
    const paymentsToRaise = initialSignedSimulation.simulation.customerPaymentOperations
      .filter((p) => p.operation.effectiveDate.getTime() >= fromDate.getTime())
      .slice(0, newNumber)

    setValue(
      'paymentToRaiseIds',
      paymentsToRaise.map((p) => p.operation.id),
      { shouldValidate: true },
    )
  }

  const onSubmitForm: FormEventHandler = (e) => {
    e.preventDefault()
    if (newSimulationWithSignature && !isPending) {
      onConfirm(newSimulationWithSignature)
      closeDialog()
    }
  }

  return (
    <FormDialog
      {...dialogProps}
      onConfirm={onSubmitForm}
      title={t('loan.payment.moveCalendarDialog.title')}
      disabled={isPending || isLockedByOther || !isValid}
    >
      {error && <ErrorAlert error={error} />}
      <Stack spacing={2}>
        <ControlledSelect
          name="fromDate"
          control={control}
          options={paymentOptions}
          parseValue={(v) => (v ? new DateOnly(v) : null)}
          label={t('loan.payment.moveCalendarDialog.from')}
          disableEmptyOption
          disabled={isPending || isLockedByOther}
        />
        <ControlledDateOnlyPicker
          name="toDate"
          control={control}
          label={t('loan.payment.moveCalendarDialog.toDate')}
          disabled={isPending || isLockedByOther || fromDate === null}
          minDate={minShiftPaymentDate}
          maxDate={maxShiftPaymentDate}
          defaultValue={fromDate}
        />
        <ControlledTextField
          label={t('loan.payment.moveCalendarDialog.reason')}
          name="reason"
          control={control}
          disabled={isPending || isLockedByOther}
        />
        <NumberField
          name="numberOfPaymentsToRaise"
          value={numberOfPaymentsToRaise}
          label={t('loan.payment.moveCalendarDialog.numberOfPaymentsToRaise')}
          disabled={isPending || isLockedByOther || !moveToFutur}
          onChange={(event) => handleOnNumberOfPaymentsToRaiseChanged(event.target.value)}
          disableDecimals
          enableNegative={false}
        />
        <NumberField
          name="amountToRaise"
          value={amountToRaise}
          label={t('loan.payment.moveCalendarDialog.amountToRaiseFor')}
          disabled={true}
        />
      </Stack>
      <LoanCalendarActionPreviewTable
        simulationBefore={originalSimulation}
        simulationAfter={newSimulationWithSignature.simulation}
        actionDate={toDate}
      />
    </FormDialog>
  )
}

export default LoanMoveCalendarDialog
